import { useCallback, useEffect, useMemo } from 'react';
import { FieldErrors, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SidebarTabNamesType } from '@pages/Admin/Campaign/ui/types';
import { useStartStopContext } from '@features/Admin/Campaign/contexts/useStartStop.context';
import {
  AgentRemovedSettingsBlockerInput,
  AgentSettingsCheckStatus,
  getCampaignListRunControl,
  getCampaignStartBlockers,
  hasCampaignDialTargets,
  getIssuedTabsFromBlockers,
  getPrimaryDisabledReason,
  getTabIssueMessages,
  isCampaignFinishing,
} from '@features/Admin/Campaign/utils/campaign-validation.utils';
import { CampaignDto, SipTrunkResponseDto } from '@shared/services/apiService/apiService';
import { CampaignValidationFields } from '@shared/types/admin/campaign';

import { CreateCampaignFormValue } from '../model/type';

interface UseCampaignStartValidationOptions {
  campaignData?: CampaignDto;
  hasRealCustomers: boolean;
  campaignSipTrunk?: SipTrunkResponseDto;
  formStateErrors: FieldErrors<CreateCampaignFormValue>;
  hasAgents: boolean;
  isLoading: boolean;
  isLoadingCampaignAgents: boolean;
  isRunningLoading: boolean;
  isStoppingLoading: boolean;
  currentUserRole?: string;
  agentsWithRemovedSettings?: AgentRemovedSettingsBlockerInput[];
  agentSettingsCheck?: AgentSettingsCheckStatus;
  setError: UseFormSetError<CreateCampaignFormValue>;
  clearErrors: UseFormClearErrors<CreateCampaignFormValue>;
}

export const useCampaignStartValidation = ({
  campaignData,
  hasRealCustomers: realCustomerExist,
  campaignSipTrunk,
  formStateErrors,
  hasAgents,
  isLoading,
  isLoadingCampaignAgents,
  isRunningLoading,
  isStoppingLoading,
  currentUserRole,
  agentsWithRemovedSettings,
  agentSettingsCheck,
  setError,
  clearErrors,
}: UseCampaignStartValidationOptions) => {
  const { t } = useTranslation();
  const { setIsDisabledCampaignRun, setDisabledReason } = useStartStopContext();

  const hasConfiguredPhoneNumbers = useMemo(
    () =>
      hasCampaignDialTargets({
        campaignType: campaignData?.type,
        hasCustomers: realCustomerExist,
        hasSipNumbers: Boolean(campaignSipTrunk?.numbers?.length),
      }),
    [campaignData, campaignSipTrunk, realCustomerExist],
  );

  const campaignStartBlockers = useMemo(
    () =>
      getCampaignStartBlockers({
        isRunningLoading,
        isLoading,
        isLoadingCampaignAgents,
        isStoppingLoading,
        campaignStatus: campaignData?.status as string,
        campaignType: campaignData?.type,
        hasAgents,
        hasConfiguredPhoneNumbers,
        currentUserRole,
        formErrors: formStateErrors,
        agentsWithRemovedSettings,
        agentSettingsCheck,
      }),
    [
      isRunningLoading,
      isLoading,
      isLoadingCampaignAgents,
      isStoppingLoading,
      campaignData?.status,
      hasAgents,
      hasConfiguredPhoneNumbers,
      currentUserRole,
      formStateErrors,
      agentsWithRemovedSettings,
      agentSettingsCheck,
    ],
  );

  const issuedTabs = useMemo(
    () => getIssuedTabsFromBlockers(campaignStartBlockers),
    [campaignStartBlockers],
  );

  const tabIssueMessages = useMemo(
    () => getTabIssueMessages(campaignStartBlockers, t),
    [campaignStartBlockers, t],
  );

  const forceValidate = useCallback(() => {
    if (!hasAgents) {
      setError('agents' as CampaignValidationFields, { type: 'required' });
      return;
    }
    clearErrors('agents' as CampaignValidationFields);
  }, [clearErrors, hasAgents, setError]);

  useEffect(() => {
    if (!campaignData) return;

    if (!hasAgents) {
      setError('agents' as CampaignValidationFields, { type: 'required' });
    } else {
      clearErrors('agents' as CampaignValidationFields);
    }
  }, [campaignData, clearErrors, hasAgents, setError]);

  useEffect(() => {
    const status = campaignData?.status as string | undefined;
    if (status === 'active' || isCampaignFinishing(status)) {
      const control = getCampaignListRunControl({ status, currentUserRole }, t);
      setIsDisabledCampaignRun(control.disabled);
      setDisabledReason(control.reason);
      return;
    }

    setIsDisabledCampaignRun(campaignStartBlockers.length > 0);
    setDisabledReason(getPrimaryDisabledReason(campaignStartBlockers, t));
  }, [
    campaignData?.status,
    campaignStartBlockers,
    currentUserRole,
    setDisabledReason,
    setIsDisabledCampaignRun,
    t,
  ]);

  return {
    campaignStartBlockers,
    issuedTabs: issuedTabs as SidebarTabNamesType[],
    tabIssueMessages,
    forceValidate,
  };
};
